// engine/save.ts — versioned localStorage save with a migration chain.
// Contract (ARCHITECTURE §6): load() = parse -> migrate -> validate -> return.
// Corrupt or absent save yields a fresh profile. NEVER throws at boot.

import type { SaveData } from '../types'
import { hasQueryFlag } from './rng'

const KEY = 'gauntlite.save'
export const SAVE_VERSION = 1

export function freshSave(): SaveData {
  return {
    version: SAVE_VERSION,
    embers: 0,
    crests: 0,
    shrine: {},
    shrineToggles: {},
    stock: [],
    loadout: { weapon: 'cinderblade', talisman: null },
    feats: {},
    featsClaimed: [],
    zonesReached: 0,
    stats: {
      runs: 0,
      deaths: 0,
      wins: 0,
      bestZone: 0,
      bestTimeMs: 0,
      kills: 0,
      embersLifetime: 0,
    },
    settings: { volume: 0.7, shake: 1 },
  }
}

// Migration chain. migrations[v] upgrades a save at version v to version v+1.
// Every future schema change adds one function here in the same commit.
const migrations: Record<number, (d: Record<string, unknown>) => void> = {
  // 1: (d) => { d.something = ...; d.version = 2 },
}

function num(v: unknown, dflt: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : dflt
}

/** Fill in anything missing/garbled so downstream code can trust the shape. */
function validate(raw: Record<string, unknown>): SaveData {
  const d = freshSave()
  d.embers = Math.max(0, Math.floor(num(raw.embers, 0)))
  d.crests = Math.max(0, Math.floor(num(raw.crests, 0)))
  if (raw.shrine && typeof raw.shrine === 'object') d.shrine = raw.shrine as Record<string, number>
  if (raw.shrineToggles && typeof raw.shrineToggles === 'object') {
    d.shrineToggles = raw.shrineToggles as Record<string, 'a' | 'b'>
  }
  if (Array.isArray(raw.stock)) d.stock = raw.stock.filter((s) => typeof s === 'string')
  if (raw.loadout && typeof raw.loadout === 'object') {
    const l = raw.loadout as Record<string, unknown>
    d.loadout = {
      weapon: typeof l.weapon === 'string' ? l.weapon : 'cinderblade',
      talisman: typeof l.talisman === 'string' ? l.talisman : null,
    }
  }
  if (raw.feats && typeof raw.feats === 'object') d.feats = raw.feats as Record<string, number>
  if (Array.isArray(raw.featsClaimed)) {
    d.featsClaimed = raw.featsClaimed.filter((s) => typeof s === 'string')
  }
  d.zonesReached = Math.max(0, Math.floor(num(raw.zonesReached, 0)))
  if (raw.stats && typeof raw.stats === 'object') {
    const s = raw.stats as Record<string, unknown>
    d.stats = {
      runs: num(s.runs, 0),
      deaths: num(s.deaths, 0),
      wins: num(s.wins, 0),
      bestZone: num(s.bestZone, 0),
      bestTimeMs: num(s.bestTimeMs, 0),
      kills: num(s.kills, 0),
      embersLifetime: num(s.embersLifetime, 0),
    }
  }
  if (raw.settings && typeof raw.settings === 'object') {
    const s = raw.settings as Record<string, unknown>
    d.settings = {
      volume: Math.min(1, Math.max(0, num(s.volume, 0.7))),
      shake: Math.min(2, Math.max(0, num(s.shake, 1))),
    }
  }
  d.version = SAVE_VERSION
  return d
}

let ephemeral = false

/** ?fresh=1 boots an untouched profile and never reads or writes localStorage. */
export function isEphemeral(): boolean {
  return ephemeral
}

export function load(): SaveData {
  ephemeral = hasQueryFlag('fresh')
  if (ephemeral) return freshSave()
  let text: string | null = null
  try {
    text = window.localStorage.getItem(KEY)
  } catch {
    return freshSave()
  }
  if (!text) return freshSave()
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') return freshSave()
    let v = Math.floor(num(parsed.version, 0))
    let guard = 0
    while (v < SAVE_VERSION && guard++ < 64) {
      const m = migrations[v]
      if (m) m(parsed)
      v++
      parsed.version = v
    }
    return validate(parsed)
  } catch {
    return freshSave()
  }
}

export function save(data: SaveData): void {
  if (ephemeral) return
  try {
    window.localStorage.setItem(KEY, JSON.stringify(data))
  } catch {
    /* storage full or blocked — never throw at the player */
  }
}

export function wipe(): SaveData {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
  return freshSave()
}
